import { Trans } from "react-i18next";
import React, { Suspense, lazy, useEffect } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import {
  FaBackward,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaForward,
  FaPause,
  FaPlay,
  FaRulerHorizontal,
  FaStepBackward,
  FaStop,
  FaVideo,
} from "react-icons/fa";
import EEGNavigationTimelineStack from "./EEGNavigationTimelineStack";
import { resolvePagePlayheadAbsoluteTime } from "../utils/pagePlayhead";
import {
  PlaybackControlButton,
  PlaybackControlCluster,
} from "./EEGPlaybackControls";
import { markEegStartup } from "../utils/eegStartupTelemetry";
import { useEEGViewerRuntime } from "./EEGViewerRuntimeContext";

const EEGClinicalOverlays = lazy(() => import("./EEGClinicalOverlays"));
const EEGSegmentInspector = lazy(() => import("./EEGSegmentInspector"));
const EegClipScissorsModal = lazy(() => import("./EegClipScissorsModal"));
const MacroOverviewZoomSelect = lazy(() => import("./MacroOverviewZoomSelect"));

/**
 * EEGPlaybackCore — first-playable slice of EEGViewer (Sprint 4A extraction).
 *
 * Statically imported (unlike EEGViewerDeferredChrome) so the waveform canvas
 * and the playback transport bar are interactive as soon as EEGViewer mounts,
 * without waiting on the heavier clinical review / dock chrome to load.
 *
 * Mounted twice by EEGViewer — once with slot="waveform" inside `.eeg-content`
 * (a flex ROW alongside the clinical dock / right dock siblings) and once with
 * slot="transport" inside `.eeg-viewer-bottom-chrome` (the fixed-height footer
 * below the events/trends bottom dock). This preserves the original flex
 * layout exactly (see EEGViewer.css ".eeg-content" / ".eeg-viewer-bottom-chrome")
 * while still keeping canvasRef attached to exactly one <canvas> element.
 */
export type EEGPlaybackCoreSlot = "waveform" | "transport";

// Permissive bag typing for this extraction sprint — see EEGViewerRuntimeContext.
type EEGPlaybackCoreRuntime = Record<string, any>;

export default function EEGPlaybackCore({
  slot,
}: {
  slot: EEGPlaybackCoreSlot;
}) {
  const runtime = useEEGViewerRuntime<EEGPlaybackCoreRuntime>();
  const {
    annotationOverlayOpacity,
    applyGoToTimeInput,
    bottomDockPanelKeys,
    calipersActive,
    canvasHeightRef,
    canvasRef,
    channelGainMenu,
    channelGainMenuRef,
    channelGainOverrides,
    channelScrollbarTrackRef,
    channelScrollOffset,
    channelScrollThumbHeightPercent,
    channelScrollThumbTopPercent,
    clampNumber,
    clearTraceFocusLock,
    clipScissorsModalOpen,
    closeChannelGainMenu,
    createAnnotationMutation,
    createSegmentAnnotation,
    currentConfig,
    currentPlaybackSpeed,
    currentRecordingId,
    currentTime,
    currentTimeRef,
    dataTransferMetrics,
    dockBottomCollapsed,
    dockDerivedEvents,
    eegData,
    eegDataError,
    eegSelection,
    eegSelectionDraft,
    eegSelectionMetrics,
    effectiveShowNavigationTimeTicks,
    eventMarkerPopoverDismissSignal,
    expandedSpacingChannelLabel,
    exportSelectedSegment,
    finePanStepSeconds,
    formatViewerClockTime,
    getSegmentSelectionOverlayStyle,
    goToStart,
    goToTimeDisplayValue,
    goToTimeHoverLabel,
    goToTimeInputFocused,
    goToTimeInputRef,
    handleChannelAutoScaleSelect,
    handleChannelExpandedSpacingSelect,
    handleChannelGainPresetSelect,
    handleChannelHideSelect,
    handleChannelIsolateSelect,
    handleChannelScrollbarMouseDown,
    handleChannelShowAllSelect,
    handleConfigChange,
    handleResetAllGainsSelect,
    handleResetChannelAppearanceSelect,
    handleResetChannelGainSelect,
    handleResetExpandedSpacingSelect,
    handleTimelineAnnotationClick,
    handleTimelineSeek,
    handleToggleNavigationTimeTicks,
    handleToggleTraceFocusLockSelect,
    handleWaveformAnnotationApprove,
    handleWaveformAnnotationAssignChannels,
    handleWaveformAnnotationAssignSelectedChannels,
    handleWaveformAnnotationEditNotes,
    handleWaveformAnnotationTimingUpdate,
    handleWaveformCanvasContextMenu,
    hasChannelOverflow,
    hasVideo,
    hiddenChannels,
    highSpeedDegradationActive,
    isFetchingEegData,
    isPlaybackRunning,
    isPlaying,
    jumpToNextEvent,
    jumpToPrevEvent,
    jumpToViewerTime,
    lockedTraceFocusLabel,
    macroOverviewCollapsed,
    macroOverviewCustomRange,
    macroOverviewZoomMode,
    maxChannelScrollOffset,
    measurementMode,
    navigateByFinePan,
    navigateByPages,
    navigationTimelineAvailable,
    navigationTimelineRef,
    nextEventCount,
    overflowPlaybackSpeedOptions,
    pageNavigationStepSeconds,
    pagePlayheadOffsetSeconds,
    pendingPausedSeekTimeRef,
    playbackControlsRef,
    playbackSpeedStripRef,
    playheadRef,
    plotLeftPx,
    previousEventCount,
    primaryPlaybackSpeedOptions,
    queryClient,
    recordingDuration,
    recordingStartMs,
    rendererMode,
    resolveEEGViewerRequestedTime,
    reviewedRanges,
    rendererRef,
    segmentInspectorCollapsed,
    segmentInspectorStatus,
    selectedClipSegmentAnnotation,
    selectedTraceChannels,
    setAnnotationOverlayError,
    setAnnotationPanelTab,
    setCalipersActive,
    setClipScissorsModalOpen,
    setCurrentTime,
    setDataWindowStart,
    setDockBottomActiveTab,
    setDockBottomCollapsed,
    setEegSelection,
    setEventMarkerPopoverOpen,
    setGoToTimeInput,
    setGoToTimeInputFocused,
    setMacroOverviewCollapsed,
    setMacroOverviewCustomRange,
    setMacroOverviewZoomMode,
    setPlaybackRunning,
    closeSegmentInspector,
    setSegmentInspectorCollapsed,
    setShowAnnotationPanel,
    setShowExtendedPlaybackSpeeds,
    setShowVideoPanel,
    shareSelectedSegment,
    showExtendedPlaybackSpeeds,
    showMacroOverviewZoomControl,
    showNavigationTimeTicksToggle,
    showTimeline,
    showVideoPanel,
    startDockTransition,
    startLockedTraceMouseNavigation,
    stopPlayback,
    studyId,
    studyVideoWarmup,
    t,
    timelineAnnotations,
    timelineRecordingDuration,
    visibleMontageChannelDetails,
    waveformAnnotationMarkers,
    workspaceTheme,
    clinicalOverlaysReady,
  } = runtime;

  useEffect(() => {
    if (slot === "transport") {
      markEegStartup("playbackControlsInteractive");
    }
  }, [slot]);

  useEffect(() => {
    if (slot !== "waveform") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setAttribute("elementtiming", "eeg-waveform-lcp");
  }, [slot, canvasRef]);

  if (slot === "transport") {
    return (
      <>
              <div className="playback-controls" ref={playbackControlsRef}>
              {showMacroOverviewZoomControl && clinicalOverlaysReady ? (
                <Suspense fallback={null}>
                  <MacroOverviewZoomSelect
                    value={macroOverviewZoomMode}
                    onChange={setMacroOverviewZoomMode}
                    className="eeg-macro-overview-zoom-select--playback"
                  />
                </Suspense>
              ) : null}
              {/* Video toggle stays on the transport bar (not deferred chrome) so
                  soak/clinician flows can open the panel even before overlays load.
                  Always expose the control; studies without video still open the panel. */}
              <PlaybackControlCluster
                className="eeg-playback-cluster--video"
                ariaLabel="Video controls"
              >
                <PlaybackControlButton
                  data-testid="eeg-video-panel-toggle"
                  onClick={() => setShowVideoPanel(!showVideoPanel)}
                  className={`video-toggle-btn ${showVideoPanel ? "active" : ""}${studyVideoWarmup?.isReady ? " video-toggle-btn--ready" : ""}${studyVideoWarmup?.isWarming ? " video-toggle-btn--warming" : ""}`}
                  aria-pressed={showVideoPanel}
                  aria-label={
                    showVideoPanel
                      ? "Hide video panel"
                      : hasVideo && studyVideoWarmup?.isReady
                        ? "Show video panel"
                        : "Show video panel"
                  }
                  icon={<FaVideo />}
                >
                  {showVideoPanel ? "Hide Video" : "Show Video"}
                </PlaybackControlButton>
              </PlaybackControlCluster>

              {/* ── Group 1: Backward navigation ──────────────────────────── */}
              <PlaybackControlCluster
                className="eeg-playback-cluster--nav"
                ariaLabel="Backward navigation controls"
              >
                <PlaybackControlButton
                  onClick={goToStart}
                  disabled={currentTime <= 0}
                  title="Jump to beginning (Home)"
                  icon={<FaStepBackward />}
                >
                  <Trans ns="features" i18nKey="ui.copy.952f375412e8" />
                </PlaybackControlButton>
                <PlaybackControlButton
                  onClick={() => navigateByPages(-1, 1)}
                  disabled={currentTime <= 0}
                  title={`Previous page - ${pageNavigationStepSeconds}s (PageUp)`}
                  aria-label={`Go back one page by ${pageNavigationStepSeconds} seconds`}
                  icon={<FaBackward />}
                >
                  <Trans ns="features" i18nKey="ui.copy.fb06270f7c21" />
                  {pageNavigationStepSeconds}s
                </PlaybackControlButton>
                <PlaybackControlButton
                  onClick={() => navigateByFinePan(-1)}
                  disabled={currentTime <= 0}
                  title={`Fine pan left - ${finePanStepSeconds}s (←)`}
                  aria-label={`Fine pan left by ${finePanStepSeconds} seconds`}
                  icon={<FaChevronLeft />}
                >
                  <Trans ns="features" i18nKey="ui.copy.8c9ce33191cd" />
                  {finePanStepSeconds}s
                </PlaybackControlButton>
              </PlaybackControlCluster>

              {/* ── Group 2: Transport ────────────────────────────────────── */}
              <PlaybackControlCluster
                className="eeg-playback-cluster--transport"
                ariaLabel="Playback transport controls"
              >
                <PlaybackControlButton
                  data-testid="eeg-playback-toggle"
                  className="eeg-playback-control--primary"
                  onClick={() => setPlaybackRunning(!isPlaybackRunning)}
                  icon={isPlaybackRunning ? <FaPause /> : <FaPlay />}
                  aria-pressed={isPlaybackRunning}
                  aria-label={
                    isPlaybackRunning ? "Pause EEG playback" : "Play EEG playback"
                  }
                >
                  {isPlaybackRunning ? "Pause" : "Play"}
                </PlaybackControlButton>
                {/* Speed button strip — three clinical zones: careful / rapid / screening */}
                {(() => {
                  const currentSpeed = currentPlaybackSpeed;
                  const getZone = (s: number) =>
                    s <= 8 ? "careful" : s <= 60 ? "rapid" : "screening";
                  const renderSpeedButtons = (
                    speeds: number[],
                    options?: { collapseAfterClick?: boolean },
                  ) =>
                    speeds.map((speed, i) => {
                      const zone = getZone(speed);
                      const prevZone = i > 0 ? getZone(speeds[i - 1]) : zone;
                      return (
                        <React.Fragment key={speed}>
                          {i > 0 && zone !== prevZone && (
                            <span
                              className="eeg-speed-zone-sep"
                              aria-hidden="true"
                            />
                          )}
                          <button
                            type="button"
                            data-testid={`eeg-speed-${speed}x`}
                            data-speed={speed}
                            className={`eeg-speed-btn eeg-speed-btn--${zone}${Math.abs(currentSpeed - speed) < 0.001 ? " active" : ""}`}
                            onClick={() => {
                              handleConfigChange({ playback_speed: speed });
                              if (options?.collapseAfterClick) {
                                setShowExtendedPlaybackSpeeds(false);
                              }
                            }}
                            title={`${speed}× — ${zone === "careful" ? "Careful review" : zone === "rapid" ? "Rapid review" : "Screening survey"}`}
                            aria-pressed={
                              Math.abs(currentSpeed - speed) < 0.001
                            }
                          >
                            {speed}×
                          </button>
                        </React.Fragment>
                      );
                    });
                  return (
                    <span
                      ref={playbackSpeedStripRef}
                      className={`eeg-speed-btn-strip${showExtendedPlaybackSpeeds ? " is-expanded" : ""}`}
                      role="group"
                      aria-label="Playback speed"
                    >
                      <span className="eeg-speed-btn-strip__primary">
                        {renderSpeedButtons(primaryPlaybackSpeedOptions)}
                        {overflowPlaybackSpeedOptions.length > 0 && (
                          <button
                            type="button"
                            className="eeg-speed-btn eeg-speed-btn-more"
                            aria-label={
                              showExtendedPlaybackSpeeds
                                ? "Hide additional playback speeds"
                                : `Show ${overflowPlaybackSpeedOptions.length} more playback speeds`
                            }
                            aria-expanded={showExtendedPlaybackSpeeds}
                            title={
                              showExtendedPlaybackSpeeds
                                ? "Hide additional playback speeds"
                                : `Show ${overflowPlaybackSpeedOptions.length} more playback speeds`
                            }
                            onClick={() =>
                              setShowExtendedPlaybackSpeeds((value: any) => !value)
                            }
                          >
                            <span className="eeg-speed-btn-more__label">
                              {showExtendedPlaybackSpeeds
                                ? "Less"
                                : `+${overflowPlaybackSpeedOptions.length}`}
                            </span>
                            <span
                              className="eeg-playback-control__icon"
                              aria-hidden="true"
                            >
                              {showExtendedPlaybackSpeeds ? (
                                <FaChevronUp />
                              ) : (
                                <FaChevronDown />
                              )}
                            </span>
                          </button>
                        )}
                      </span>
                      {overflowPlaybackSpeedOptions.length > 0 && (
                        <span className="eeg-speed-btn-strip__secondary">
                          {renderSpeedButtons(overflowPlaybackSpeedOptions, {
                            collapseAfterClick: true,
                          })}
                        </span>
                      )}
                    </span>
                  );
                })()}
                <PlaybackControlButton
                  onClick={stopPlayback}
                  title="Stop playback (S)"
                  icon={<FaStop />}
                >
                  <Trans ns="features" i18nKey="ui.copy.9e253470c876" />
                </PlaybackControlButton>
              </PlaybackControlCluster>

              {/* ── Group 3: Forward navigation ───────────────────────────── */}
              <PlaybackControlCluster
                className="eeg-playback-cluster--nav eeg-playback-cluster--forward"
                ariaLabel="Forward navigation controls"
              >
                <PlaybackControlButton
                  onClick={() => navigateByFinePan(1)}
                  disabled={
                    recordingDuration > 0 &&
                    currentTime >=
                      Math.max(
                        0,
                        recordingDuration - (currentConfig?.time_scale || 10),
                      )
                  }
                  title={`Fine pan right - ${finePanStepSeconds}s (→)`}
                  aria-label={`Fine pan right by ${finePanStepSeconds} seconds`}
                  endIcon={<FaChevronRight />}
                >
                  <Trans ns="features" i18nKey="ui.copy.8c9ce33191cd" />
                  {finePanStepSeconds}s
                </PlaybackControlButton>
                <PlaybackControlButton
                  onClick={() => navigateByPages(1, 1)}
                  disabled={
                    recordingDuration > 0 &&
                    currentTime >=
                      Math.max(
                        0,
                        recordingDuration - (currentConfig?.time_scale || 10),
                      )
                  }
                  title={`Next page - ${pageNavigationStepSeconds}s (PageDown)`}
                  aria-label={`Advance one page by ${pageNavigationStepSeconds} seconds`}
                  endIcon={<FaForward />}
                >
                  <Trans ns="features" i18nKey="ui.copy.fb06270f7c21" />
                  {pageNavigationStepSeconds}s
                </PlaybackControlButton>
              </PlaybackControlCluster>

              {/* ── Group 4: Event navigation (deferred post-playable) ─────── */}
              {clinicalOverlaysReady ? (
              <PlaybackControlCluster
                className="eeg-playback-cluster--events"
                ariaLabel="Event navigation controls"
              >
                <PlaybackControlButton
                  className="eeg-playback-btn-event"
                  onClick={jumpToPrevEvent}
                  disabled={previousEventCount === 0}
                  title="Previous event (P)"
                  aria-label="Jump to previous event"
                  icon={<FaChevronLeft />}
                  suffix={
                    <span className="eeg-playback-btn-count">
                      {previousEventCount}
                    </span>
                  }
                >
                  <Trans ns="features" i18nKey="ui.copy.ad8919ace091" />
                </PlaybackControlButton>
                <PlaybackControlButton
                  className="eeg-playback-btn-event"
                  onClick={jumpToNextEvent}
                  disabled={nextEventCount === 0}
                  title="Next event (N)"
                  aria-label="Jump to next event"
                  endIcon={<FaChevronRight />}
                  suffix={
                    <span className="eeg-playback-btn-count">
                      {nextEventCount}
                    </span>
                  }
                >
                  <Trans ns="features" i18nKey="ui.copy.ad8919ace091" />
                </PlaybackControlButton>
              </PlaybackControlCluster>
              ) : null}

              {/* ── Group 5: Go-to time + timeline toggle ─────────────────── */}
              {clinicalOverlaysReady ? (
              <PlaybackControlCluster
                className="eeg-playback-cluster--position"
                ariaLabel="Playback position controls"
              >
                <input
                  ref={goToTimeInputRef}
                  type="text"
                  className={`eeg-go-to-time-input${
                    goToTimeInputFocused ? "" : " eeg-go-to-time-input--compact"
                  }`}
                  value={goToTimeDisplayValue}
                  onFocus={() => {
                    setGoToTimeInput(
                      formatViewerClockTime(
                        resolvePagePlayheadAbsoluteTime({
                          viewportStartSeconds: resolveEEGViewerRequestedTime({
                            currentTime: currentTimeRef.current,
                            pendingSeekTime: pendingPausedSeekTimeRef.current,
                          }),
                          offsetSeconds: pagePlayheadOffsetSeconds || 0,
                          timeScaleSeconds: currentConfig?.time_scale || 10,
                        }),
                      ),
                    );
                    setGoToTimeInputFocused(true);
                  }}
                  onBlur={(event) => {
                    setGoToTimeInputFocused(false);
                    applyGoToTimeInput(event.currentTarget.value);
                  }}
                  onChange={(event) => setGoToTimeInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === "Tab") {
                      event.preventDefault();
                      applyGoToTimeInput(event.currentTarget.value);
                      goToTimeInputRef.current?.blur();
                    }
                  }}
                  placeholder={t("ui.copy.k0l1m2n3o4p5", {
                    defaultValue: "Go to time",
                  })}
                  title={`${t("ui.copy.k0l1m2n3o4p5", { defaultValue: "Go to time" })} — ${goToTimeHoverLabel} (HH:MM:SS, MM:SS, or seconds) - Ctrl+G`}
                  aria-label={t("ui.copy.k0l1m2n3o4p5", {
                    defaultValue: "Go to time",
                  })}
                />
                {bottomDockPanelKeys.length === 0 &&
                  showNavigationTimeTicksToggle && (
                  <PlaybackControlButton
                    onClick={handleToggleNavigationTimeTicks}
                    className={`timeline-toggle-btn eeg-ruler-toggle ${showTimeline ? "active" : ""}`}
                    title={
                      showTimeline
                        ? t("ui.copy.e5f6a7b8c9d0")
                        : t("ui.copy.d4e5f6a7b8c9")
                    }
                    aria-label={
                      showTimeline
                        ? t("ui.copy.e5f6a7b8c9d0")
                        : t("ui.copy.d4e5f6a7b8c9")
                    }
                    icon={<FaRulerHorizontal />}
                  >
                    <Trans ns="features" i18nKey="ui.copy.c2281cecb873" />
                  </PlaybackControlButton>
                )}
              </PlaybackControlCluster>
              ) : null}

              {selectedClipSegmentAnnotation &&
                currentRecordingId &&
                clinicalOverlaysReady && (
                <Suspense fallback={null}>
                  <EegClipScissorsModal
                  show={clipScissorsModalOpen}
                  recordingId={currentRecordingId}
                  annotationId={selectedClipSegmentAnnotation.annotation_id as string}
                  annotationLabel={
                    selectedClipSegmentAnnotation.label || "Clip Segment"
                  }
                  onHide={() => setClipScissorsModalOpen(false)}
                  onCompleted={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ["eeg-study", studyId],
                    });
                    void queryClient.invalidateQueries({
                      queryKey: ["eeg-recordings", studyId],
                    });
                  }}
                />
                </Suspense>
              )}

              <div
                className="playback-controls__trail"
                aria-label="Viewer status"
              >
                <span className="playback-controls__metrics">
                  {dataTransferMetrics && (
                    <>
                      <span>
                        {(dataTransferMetrics.size / 1024 / 1024).toFixed(2)}{" "}
                        <Trans ns="features" i18nKey="ui.copy.6e979f426b67" />
                      </span>
                      <span className="playback-controls__metrics-sep">·</span>
                      <span>
                        {dataTransferMetrics.time.toFixed(0)}
                        <Trans ns="features" i18nKey="ui.copy.26cc3217be64" />
                      </span>
                      <span className="playback-controls__metrics-sep">·</span>
                      <span>L{dataTransferMetrics.level}</span>
                    </>
                  )}
                </span>
                {process.env.NODE_ENV === "development" && (
                  <span className="playback-controls__perf-hint">
                    <Trans ns="features" i18nKey="ui.copy.ea683ad61606" />
                    <kbd>
                      <Trans ns="features" i18nKey="ui.copy.f2f9dd89ac2c" />
                    </kbd>{" "}
                    <Trans ns="features" i18nKey="ui.copy.f47039fbb1f7" />
                  </span>
                )}
                <span
                  className={`eeg-renderer-mode-badge eeg-renderer-mode-badge--${rendererMode.mode}`}
                  title={
                    rendererMode.mode === "cpu" &&
                    rendererMode.fallbackReason !== "none"
                      ? `Canvas 2D (WebGL2 unavailable: ${rendererMode.fallbackReason.replaceAll("_", " ")})`
                      : rendererMode.mode === "gpu"
                        ? "WebGL2 active — envelope bands rendered via WebGL2 LINES draw call; annotations and grid use Canvas 2D overlay"
                        : undefined
                  }
                >
                  {rendererMode.mode === "gpu" ? "WebGL2 ✓" : "Canvas 2D"}
                </span>
                {dataTransferMetrics && dataTransferMetrics.time >= 1000 && (
                  <span
                    className={`eeg-renderer-badge eeg-renderer-badge--${
                      dataTransferMetrics.time < 3000 ? "normal" : "slow"
                    }`}
                  >
                    {dataTransferMetrics.time < 3000 ? "Normal" : "Slow"}
                  </span>
                )}
                {highSpeedDegradationActive ? (
                  <span
                    className="eeg-degraded-mode-chip"
                    data-testid="eeg-degraded-mode-chip"
                    role="status"
                    aria-live="polite"
                  >
                    Rapid review: overlays reduced · lower detail (LOD)
                  </span>
                ) : null}
              </div>
            </div>

      </>
    );
  }

  return (
    <>
              <div className="eeg-waveform">
                <div
                  className="waveform-display"
                  onMouseDownCapture={(event) => {
                    const target = event.target as HTMLElement | null;
                    if (
                      target?.closest(
                        "button, input, select, textarea, [role='button'], .eeg-channel-gain-menu, .eeg-channel-scrollbar, .eeg-segment-inspector",
                      )
                    ) {
                      return;
                    }
                    startLockedTraceMouseNavigation(event.nativeEvent);
                  }}
                  onContextMenu={(event) => {
                    const target = event.target as Node | null;
                    if (
                      target &&
                      channelGainMenuRef.current?.contains(target)
                    ) {
                      return;
                    }
                    handleWaveformCanvasContextMenu(event.nativeEvent);
                  }}
                >
                  {isFetchingEegData && !eegData && (
                    <div className="waveform-loading-overlay">
                      <Spinner animation="border" variant="light" size="sm" />
                      <span>
                        <Trans ns="features" i18nKey="ui.copy.5e54b2ad3fd8" />
                      </span>
                    </div>
                  )}
                  {isFetchingEegData && eegData && (
                    <div className="waveform-fetching-indicator">
                      <Spinner animation="border" variant="light" size="sm" />
                    </div>
                  )}

                  {eegDataError && (
                    <div className="waveform-error-overlay">
                      <p>
                        <Trans ns="features" i18nKey="ui.copy.30fe71319e6d" />
                      </p>
                      <small>{(eegDataError as Error)?.message}</small>
                    </div>
                  )}

                  <canvas
                    ref={canvasRef}
                    className="waveform-canvas"
                    data-eeg-lcp-candidate="true"
                  />
                  {currentConfig && clinicalOverlaysReady ? (
                    <Suspense fallback={null}>
                      <EEGClinicalOverlays
                        canvasRef={canvasRef}
                        canvasHeight={canvasHeightRef.current}
                        currentTime={currentTime}
                        timeScale={currentConfig.time_scale}
                        samplingRate={eegData?.sampling_rate ?? 256}
                        sensitivity={currentConfig.sensitivity_value}
                        channelCount={eegData?.channels?.length ?? 1}
                        polarity={currentConfig.polarity}
                        calipersActive={calipersActive}
                        measurementMode={measurementMode}
                        onToggleCalipers={() => setCalipersActive(false)}
                        snapCaliperPoint={(point) =>
                          rendererRef?.current?.snapCaliperPoint(point) ?? null
                        }
                        selectionDraft={eegSelectionDraft}
                        selection={
                          segmentInspectorCollapsed ? null : eegSelection
                        }
                        getOverlayStyle={getSegmentSelectionOverlayStyle}
                        clampNumber={clampNumber}
                        spikes={dockDerivedEvents.spikes}
                        seizures={dockDerivedEvents.seizures}
                        channelNames={eegData?.channels || []}
                        onMarkerClick={jumpToViewerTime}
                        eventMarkerPopoverDismissSignal={
                          eventMarkerPopoverDismissSignal
                        }
                        onPopoverVisibilityChange={setEventMarkerPopoverOpen}
                        onViewInEventsPanel={() => {
                          startDockTransition(() => {
                            setDockBottomActiveTab("event_detection");
                            if (dockBottomCollapsed)
                              setDockBottomCollapsed(false);
                          });
                        }}
                        annotationMarkers={waveformAnnotationMarkers}
                        annotationOverlayOpacity={annotationOverlayOpacity}
                        maxTimeSeconds={
                          recordingDuration > 0 ? recordingDuration : undefined
                        }
                        onUpdateAnnotationTiming={
                          handleWaveformAnnotationTimingUpdate
                        }
                        onAssignSelectedChannels={
                          handleWaveformAnnotationAssignSelectedChannels
                        }
                        onAssignChannels={handleWaveformAnnotationAssignChannels}
                        onRequestEditNotes={handleWaveformAnnotationEditNotes}
                        onRequestEditAnnotation={handleWaveformAnnotationEditNotes}
                        onApproveAnnotation={handleWaveformAnnotationApprove}
                        onChannelDraftChange={() =>
                          setAnnotationOverlayError(null)
                        }
                        availableChannelLabels={visibleMontageChannelDetails.map(
                          (channel: any) => channel.label,
                        )}
                      />
                    </Suspense>
                  ) : null}
                  {!segmentInspectorCollapsed ? (
                    <Suspense fallback={null}>
                      <EEGSegmentInspector
                      selection={eegSelection}
                      metrics={eegSelectionMetrics}
                      visibleMontageChannelCount={
                        visibleMontageChannelDetails.length
                      }
                      statusMessage={segmentInspectorStatus}
                      isCreateAnnotationPending={
                        createAnnotationMutation.isPending
                      }
                      onClose={closeSegmentInspector}
                      onAnnotate={() => {
                        setShowAnnotationPanel(true);
                        setAnnotationPanelTab("annotate");
                      }}
                      onMarkSeizure={() =>
                        createSegmentAnnotation(
                          "seizure_onset",
                          "Seizure segment",
                          "#ef4444",
                        )
                      }
                      onMarkArtifact={() =>
                        createSegmentAnnotation(
                          "artifact",
                          "Artifact segment",
                          "#f59e0b",
                        )
                      }
                      onFindSimilar={() => {
                        setDockBottomActiveTab("event_detection");
                        setDockBottomCollapsed(false);
                      }}
                      onExport={exportSelectedSegment}
                      onShare={shareSelectedSegment}
                      onZoom={() => {
                        if (!eegSelection || !eegSelectionMetrics) return;
                        handleConfigChange({
                          time_scale: Math.max(
                            5,
                            Math.ceil(eegSelectionMetrics.duration),
                          ),
                        });
                        setDataWindowStart(Math.max(0, eegSelection.startTime));
                      }}
                      onJumpToSimilarEvent={(startTime) => {
                        setDataWindowStart(Math.max(0, startTime - 1));
                        setCurrentTime(startTime);
                      }}
                    />
                    </Suspense>
                  ) : null}
                  {hasChannelOverflow && (
                    <div
                      ref={channelScrollbarTrackRef}
                      className="eeg-channel-scrollbar"
                      onMouseDown={handleChannelScrollbarMouseDown}
                      role="scrollbar"
                      aria-label="Channel vertical scrollbar"
                      aria-valuemin={0}
                      aria-valuemax={maxChannelScrollOffset}
                      aria-valuenow={channelScrollOffset}
                    >
                      <div
                        className="eeg-channel-scrollbar-thumb"
                        style={{
                          height: `${channelScrollThumbHeightPercent}%`,
                          top: `${channelScrollThumbTopPercent}%`,
                        }}
                      />
                    </div>
                  )}
                  {channelGainMenu.open &&
                    channelGainMenu.channelLabel &&
                    createPortal(
                      <div
                        ref={channelGainMenuRef}
                        className="eeg-channel-gain-menu"
                        onContextMenu={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        style={{
                          left: channelGainMenu.x,
                          top: channelGainMenu.y,
                        }}
                      >
                      <div className="eeg-channel-gain-menu__title">
                        {channelGainMenu.channelLabel}
                      </div>
                      {/* EEG-CUX-001: channel visibility actions */}
                      <button
                        type="button"
                        className="eeg-channel-gain-menu__action"
                        onClick={handleChannelHideSelect}
                      >
                        <Trans ns="features" i18nKey="ui.copy.d320861712de" />
                      </button>
                      <button
                        type="button"
                        className="eeg-channel-gain-menu__action"
                        onClick={handleChannelIsolateSelect}
                      >
                        <Trans ns="features" i18nKey="ui.copy.e6f6c0289225" />
                      </button>
                      {hiddenChannels.size > 0 && (
                        <button
                          type="button"
                          className="eeg-channel-gain-menu__action eeg-channel-gain-menu__action--restore"
                          onClick={handleChannelShowAllSelect}
                        >
                          <Trans ns="features" i18nKey="ui.copy.f4519f4ad16e" />
                          {` (${hiddenChannels.size} hidden)`}
                        </button>
                      )}
                      <hr className="eeg-channel-gain-menu__divider" />
                      <button
                        type="button"
                        className="eeg-channel-gain-menu__action"
                        onClick={handleChannelExpandedSpacingSelect}
                      >
                        {channelGainMenu.channelLabel ===
                        expandedSpacingChannelLabel
                          ? "Reset expanded spacing"
                          : "Expand spacing around this channel"}
                      </button>
                      {expandedSpacingChannelLabel && (
                        <button
                          type="button"
                          className="eeg-channel-gain-menu__action eeg-channel-gain-menu__action--restore"
                          onClick={handleResetExpandedSpacingSelect}
                        >
                          <Trans ns="features" i18nKey="ui.copy.bccbc63c711f" />
                          {` (${expandedSpacingChannelLabel})`}
                        </button>
                      )}
                      <hr className="eeg-channel-gain-menu__divider" />
                      <button
                        type="button"
                        className={`eeg-channel-gain-menu__action${lockedTraceFocusLabel === channelGainMenu.channelLabel ? " eeg-channel-gain-menu__action--active" : ""}`}
                        onClick={handleToggleTraceFocusLockSelect}
                      >
                        {lockedTraceFocusLabel === channelGainMenu.channelLabel
                          ? t("ui.eegViewer.traceFocus.unlock")
                          : t("ui.eegViewer.traceFocus.lock")}
                      </button>
                      {lockedTraceFocusLabel && (
                        <button
                          type="button"
                          className="eeg-channel-gain-menu__action eeg-channel-gain-menu__action--restore"
                          onClick={() => {
                            clearTraceFocusLock();
                            closeChannelGainMenu();
                          }}
                        >
                          {t("ui.eegViewer.traceFocus.clearLock", {
                            channel: lockedTraceFocusLabel,
                          })}
                        </button>
                      )}
                      <hr className="eeg-channel-gain-menu__divider" />
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(0.1)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.11f618976177" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(0.25)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.83b4303bc3dd" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(0.5)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.bdb9f24a8db2" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(1)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.4d228fb0e951" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(2)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.5fd7a078ece4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(4)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.4449e425021e" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChannelGainPresetSelect(10)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.9a57e59c6ded" />
                      </button>
                      <button
                        type="button"
                        onClick={handleChannelAutoScaleSelect}
                      >
                        <Trans ns="features" i18nKey="ui.copy.ed80d5d811ed" />
                      </button>
                      {channelGainMenu.channelLabel &&
                        Object.prototype.hasOwnProperty.call(
                          channelGainOverrides,
                          channelGainMenu.channelLabel,
                        ) && (
                          <button
                            type="button"
                            className="eeg-channel-gain-menu__action"
                            onClick={handleResetChannelGainSelect}
                          >
                            <Trans ns="features" i18nKey="ui.copy.9c49dbbe6e86" />
                          </button>
                        )}
                      {Object.keys(channelGainOverrides).length > 0 && (
                        <button
                          type="button"
                          className="eeg-channel-gain-menu__action eeg-channel-gain-menu__action--restore"
                          onClick={handleResetAllGainsSelect}
                        >
                          <Trans ns="features" i18nKey="ui.copy.a55357879e25" />
                        </button>
                      )}
                      {(selectedTraceChannels.length > 0 ||
                        expandedSpacingChannelLabel ||
                        lockedTraceFocusLabel ||
                        Object.keys(channelGainOverrides).length > 0) && (
                        <button
                          type="button"
                          className="eeg-channel-gain-menu__action eeg-channel-gain-menu__action--restore"
                          onClick={handleResetChannelAppearanceSelect}
                        >
                          <Trans
                            ns="features"
                            i18nKey="ui.copy.resetChannelAppearance"
                          />
                        </button>
                      )}
                      </div>,
                      document.body,
                    )}
                </div>
              {/* EEG-UX-003: Navigation minimap — sibling of waveform-display so
                  canvas max-height:100% / overflow:hidden cannot clip it. */}
              {navigationTimelineAvailable && (
                <div
                  ref={navigationTimelineRef}
                  className="eeg-navigation-timeline-host"
                >
                  <EEGNavigationTimelineStack
                    totalDuration={timelineRecordingDuration}
                    recordingStartMs={recordingStartMs}
                    viewportStart={currentTime}
                    viewportEnd={
                      currentTime + (currentConfig?.time_scale || 10)
                    }
                    playheadTime={
                      currentTime + (pagePlayheadOffsetSeconds || 0)
                    }
                    playheadTimeRef={playheadRef}
                    playheadPageOffsetSeconds={pagePlayheadOffsetSeconds || 0}
                    isPlaying={isPlaying}
                    annotations={timelineAnnotations}
                    onSeek={handleTimelineSeek}
                    onAnnotationClick={handleTimelineAnnotationClick}
                    showTimeTicks={effectiveShowNavigationTimeTicks}
                    variant="compact"
                    macroZoomMode={macroOverviewZoomMode}
                    onMacroZoomModeChange={setMacroOverviewZoomMode}
                    macroCustomRange={macroOverviewCustomRange}
                    onMacroCustomRangeChange={setMacroOverviewCustomRange}
                    reviewedRanges={reviewedRanges}
                    videoSyncActive={showVideoPanel}
                    macroCollapsed={macroOverviewCollapsed}
                    onMacroCollapsedChange={setMacroOverviewCollapsed}
                    workspaceTheme={workspaceTheme}
                    plotLeftPx={plotLeftPx}
                  />
                </div>
              )}
              </div>

    </>
  );
}
